import type { SetupItem, SetupItemKey } from '@wodi/shared';

// Words for each setup item (docs/plans/phase1-home-and-demo-data.md §3), shared by the
// Home checklist and the Settings overview so both say the same thing.

type SettingsPath =
  | '/settings/service-points'
  | '/settings/numbers'
  | '/settings/services'
  | '/settings/payers'
  | '/settings/stores'
  | '/settings/users'
  | '/settings/terminology';

export interface SetupItemCopy {
  label: string;
  /** The settings page that fixes it; null when there is no screen yet. */
  to: SettingsPath | null;
  describe: (item: SetupItem) => string;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

export const SETUP_COPY: Record<SetupItemKey, SetupItemCopy> = {
  facilities: {
    label: 'Facilities',
    to: null,
    describe: ({ count, missing }) =>
      missing === 0
        ? `${plural(count, 'facility', 'facilities')}, each with an MFL code`
        : `${String(missing)} of ${plural(count, 'facility', 'facilities')} ${missing === 1 ? 'has' : 'have'} no MFL code. There is no settings screen for facilities yet.`,
  },
  service_points: {
    label: 'Service points',
    to: '/settings/service-points',
    describe: ({ count }) =>
      count === 0
        ? 'No service points yet, so patients cannot be checked in here'
        : `${plural(count, 'active service point')} here`,
  },
  number_formats: {
    label: 'Patient and visit numbers',
    to: '/settings/numbers',
    describe: ({ missing }) =>
      missing === 0
        ? 'Patient and visit number formats are set'
        : `${plural(missing, 'number format')} not set here`,
  },
  services: {
    label: 'Services and items',
    to: '/settings/services',
    describe: ({ count }) =>
      count === 0
        ? 'No services or items yet'
        : plural(count, 'active service or item', 'active services and items'),
  },
  prices: {
    label: 'Cash prices',
    to: '/settings/services',
    describe: ({ count, missing }) =>
      count === 0
        ? 'No services or items to price yet'
        : missing === 0
          ? 'Every service and item offered here has a cash price'
          : `${plural(missing, 'service or item', 'services and items')} offered here ${missing === 1 ? 'has' : 'have'} no cash price`,
  },
  payers: {
    label: 'Payers',
    to: '/settings/payers',
    describe: ({ count, missing }) =>
      count === 0
        ? 'No payers yet'
        : missing === 0
          ? `${plural(count, 'payer')}, each with a price list`
          : `${String(missing)} of ${plural(count, 'payer')} ${missing === 1 ? 'has' : 'have'} no price list`,
  },
  stores: {
    label: 'Stores',
    to: '/settings/stores',
    describe: ({ count }) =>
      count === 0 ? 'No stores here yet' : `${plural(count, 'active store')} here`,
  },
  users: {
    label: 'Users',
    to: '/settings/users',
    describe: ({ count }) =>
      count <= 1
        ? 'Only one person can work here so far'
        : `${plural(count, 'person', 'people')} can work here`,
  },
  terminology: {
    label: 'Terminology',
    to: '/settings/terminology',
    describe: ({ count, missing }) =>
      count === 0
        ? 'No code systems loaded yet'
        : missing === 0
          ? `${plural(count, 'code system')} loaded`
          : `${String(missing)} of ${plural(count, 'code system')} ${missing === 1 ? 'has' : 'have'} no active version`,
  },
};
